import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ConfigProvider } from 'antd';
import zhCN from 'antd/lib/locale/zh_CN';
import Layout from './components/Layout';

// 认证相关页面
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';

// 教师页面
import TeacherDashboard from './pages/teacher/Dashboard';
import ExerciseManager from './pages/teacher/ExerciseManager';
import CreateExercise from './pages/teacher/CreateExercise';
import AssignExercise from './pages/teacher/AssignExercise';
import StudentManager from './pages/teacher/StudentManager';

// 学生页面
import StudentDashboard from './pages/student/Dashboard';
import Exercises from './pages/student/Exercises';
import ExerciseDetail from './pages/student/ExerciseDetail';
import ExamPage from './pages/student/ExamPage';
import Profile from './pages/student/Profile';

// 上下文
import { AuthProvider, useAuth } from './contexts/AuthContext';

function AppContent() {
  const { user, isAuthenticated, loading, clearSession } = useAuth();
  const location = useLocation();

  // 在路径变化时检查权限
  useEffect(() => {
    if (isAuthenticated && user) {
      const currentPath = location.pathname;
      const isTeacherPath = currentPath.startsWith('/teacher');
      const isStudentPath = currentPath.startsWith('/student');
      
      if ((isTeacherPath && user.role !== 'teacher') || 
          (isStudentPath && user.role !== 'student')) {
        console.warn('检测到角色不匹配，正在清除会话');
        clearSession();
        window.location.href = '/login'; // 硬重定向以确保完全刷新
      }
    }
  }, [location.pathname, isAuthenticated, user, clearSession]);

  // 认证路由保护组件
  const ProtectedRoute = ({ children, allowedRoles }) => {
    if (loading) {
      return <div>加载中...</div>;
    }
    
    if (!isAuthenticated) {
      return <Navigate to="/login" replace />;
    }

    if (allowedRoles && !allowedRoles.includes(user?.role)) {
      console.warn(`用户角色 [${user?.role}] 无权访问此页面，需要角色: [${allowedRoles.join(', ')}]`);
      // 根据用户角色重定向到正确的首页
      return <Navigate to={user?.role === 'teacher' ? '/teacher' : '/student'} replace />;
    }

    return children;
  };

  if (loading) {
    return <div>加载中...</div>;
  }

  return (
    <Routes>
      {/* 公共路由 */}
      <Route path="/login" element={
        isAuthenticated ? (
          <Navigate to={user?.role === 'teacher' ? '/teacher' : '/student'} replace />
        ) : (
          <Login />
        )
      } />
      <Route path="/register" element={<Register />} />

      {/* 教师路由 */}
      <Route path="/teacher" element={
        <ProtectedRoute allowedRoles={['teacher', 'admin']}>
          <Layout />
        </ProtectedRoute>
      }>
        <Route index element={<TeacherDashboard />} />
        <Route path="exercises" element={<ExerciseManager />} />
        <Route path="create-exercise" element={<CreateExercise />} />
        <Route path="assign-exercise" element={<AssignExercise />} />
        <Route path="students" element={<StudentManager />} />
      </Route>

      {/* 学生路由 */}
      <Route path="/student" element={
        <ProtectedRoute allowedRoles={['student']}>
          <Layout />
        </ProtectedRoute>
      }>
        <Route index element={<StudentDashboard />} />
        <Route path="exercises" element={<Exercises />} />
        <Route path="exercises/:id" element={<ExerciseDetail />} />
        <Route path="profile" element={<Profile />} />
      </Route>

      {/* 学生测试页面 - 独立路由，不包含在Layout中 */}
      <Route path="/exam/:assignmentId" element={
        <ProtectedRoute allowedRoles={['student']}>
          <ExamPage />
        </ProtectedRoute>
      } />

      {/* 默认重定向 */}
      <Route path="/" element={
        isAuthenticated ? (
          <Navigate to={user?.role === 'teacher' ? '/teacher' : '/student'} replace />
        ) : (
          <Navigate to="/login" replace />
        )
      } />

      {/* 捕获所有其他路径 */}
      <Route path="*" element={
        <Navigate to="/" replace />
      } />
    </Routes>
  );
}

function App() {
  return (
    <ConfigProvider locale={zhCN}>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ConfigProvider>
  );
}

export default App;  
